/*
 * The end of the walkthrough as a game end screen (client, 2026-10-24: "like a
 * YouTube / game end screen"). It takes over the page once the visitor books
 * or skips: a dark navy stage with neon rays, light streaks, a moving grid
 * floor, circuit traces and a one-off particle burst (all decorative, all
 * static under reduced motion).
 *
 * Top: "Demo complete", a 1–3 star rating and "See you next time!". The
 * role="status" element carries the post-condition (data-trapezium-state
 * "submitted" | "skipped"); it must stay outside the private section (R13).
 * Middle: the NirogGyan mark in a glowing orb with the CTA (skipped: "Book a
 * live demo", end-book, back to the booking choice; booked: a "Demo request
 * sent" badge), the visitor's run computed from useVisited (counts tick up in
 * CSS), and "Watch next": the Smart Report showreel, click to play, muted.
 * Bottom: "Start again" (→ /, also the R key) and niroggyan.com.
 *
 * NextStep keeps this mounted (hidden) once shown, so end-book is never
 * unmounted by its own click.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Globe, Pause, Play, RotateCcw, Star, Trophy } from 'lucide-react';
import { useVisited } from '../../demo/useVisited';
import VideoMaximize from '../../ui/VideoMaximize';
import { exploreScore } from './exploreScore';
import './EndScreen.css';

const LOGO_SRC = `${process.env.PUBLIC_URL}/Niroggyan_logo.svg`;
const SITE = 'https://niroggyan.com';

const LINES = {
  submitted: "Thank you. We'll be in touch within one working day.",
  skipped: 'Thank you for exploring NirogGyan.',
};

const NEXT_VIDEO = {
  src: '/demo/video/smart-report-showreel-v2.mp4',
  poster: '/demo/video/smart-report-showreel-v2.jpg',
  title: 'Smart Report showreel (0:30)',
};

/* The arrival burst: [angle deg, distance px, size px, colour]. Decorative. */
const BURST = Array.from({ length: 18 }, (_, i) => [
  i * 20 + (i % 2 ? 7 : -5),
  i % 3 === 0 ? 190 : i % 3 === 1 ? 150 : 118,
  i % 4 === 0 ? 8 : 5,
  ['#3cd0ff', '#1f6bff', '#ffffff', '#7fb2ff', '#ffcc4d', '#4d8dff'][i % 6],
]);

/* Warp streaks shooting out from behind the headline: [angle deg, delay s]. */
const STREAKS = [
  [-168, 0],
  [-150, 1.7],
  [-24, 0.6],
  [-8, 2.3],
  [12, 1.1],
  [158, 2.8],
  [174, 0.9],
  [196, 2],
];

/* Circuit traces for the bottom corners (mirrored on the right). */
const TRACES = [
  'M0 150 H120 L150 120 H260 L282 98 H340',
  'M0 172 H170 L196 146 H300',
  'M0 128 H70 L98 100 H190 L210 80',
  'M40 196 H230 L254 172 H420',
];
const NODES = [
  [340, 98],
  [300, 146],
  [210, 80],
  [420, 172],
  [120, 150],
];

/* Keys that belong to the focused control, not to the end screen's R shortcut. */
const OWNS_KEYS = 'input, textarea, select, video, audio, [contenteditable]:not([contenteditable="false"])';
const OPEN_DIALOG = '[role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog[open]';

function Circuits({ side }) {
  return (
    <svg className={`nse-circuit nse-circuit--${side}`} viewBox="0 0 440 210" aria-hidden="true" focusable="false">
      {TRACES.map((d) => (
        <path key={d} d={d} className="nse-circuit__trace" />
      ))}
      {TRACES.map((d, i) => (
        <path key={`p${d}`} d={d} className="nse-circuit__pulse" style={{ animationDelay: `${i * 0.9}s` }} />
      ))}
      {NODES.map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="5" className="nse-circuit__node" />
      ))}
    </svg>
  );
}

function Stars({ count }) {
  return (
    <p className="nse-stars" role="img" aria-label={`Rating: ${count} of 3 stars`} data-stars={count}>
      {[1, 2, 3].map((n) => (
        <span key={n} className={n <= count ? 'nse-star is-earned' : 'nse-star'} style={{ '--i': n }}>
          <Star size={n === 2 ? 40 : 32} strokeWidth={1.8} fill="currentColor" aria-hidden="true" />
        </span>
      ))}
    </p>
  );
}

/* One line of the run. The digits tick up in CSS (@property counter); the sr-only text is the value. */
function Stat({ name, label, value, total, index }) {
  return (
    <li className="nse-stat" data-stat={name} style={{ '--p': total ? value / total : 0, '--i': index }}>
      <span className="nse-stat__label">{label}</span>
      <span className="nse-stat__value" aria-hidden="true">
        <span className="nse-count" style={{ '--nse-to': value }} />
        <span className="nse-stat__of">/{total}</span>
      </span>
      <span className="sr-only">
        {value} of {total}
      </span>
      <span className="nse-stat__bar" aria-hidden="true">
        <span />
      </span>
    </li>
  );
}

function WatchNext({ active }) {
  const videoRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [state, setState] = useState('paused');
  const [started, setStarted] = useState(false);
  const playing = state === 'playing';

  const attach = useCallback((node) => {
    videoRef.current = node;
    setVideo(node);
  }, []);

  // Leaving the end screen (back to booking) pauses the showreel.
  useEffect(() => {
    if (!active && playing) videoRef.current?.pause();
  }, [active, playing]);

  const toggle = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      return;
    }
    setStarted(true);
    const request = el.play();
    if (request && typeof request.catch === 'function') request.catch(() => setState('paused'));
  }, [playing]);

  return (
    <figure className="nse-next" data-trapezium-state={state}>
      <figcaption className="nse-tag">Watch next</figcaption>
      <div className="nse-next__frame">
        <video
          ref={attach}
          className="nse-next__video"
          src={NEXT_VIDEO.src}
          poster={NEXT_VIDEO.poster}
          preload="none"
          muted
          playsInline
          controls={started}
          aria-label={NEXT_VIDEO.title}
          onPlay={() => {
            setStarted(true);
            setState('playing');
          }}
          onPause={() => setState('paused')}
          onEnded={() => setState('paused')}
        />
        <VideoMaximize
          video={video}
          id="next-step.book-demo.maximize-next"
          label="the Smart Report showreel"
          className="nse-next__max"
        />
        <button
          type="button"
          className={playing ? 'nse-play is-playing' : 'nse-play'}
          onClick={toggle}
          data-trapezium-id="next-step.book-demo.play-next"
        >
          {playing ? (
            <>
              <Pause size={14} strokeWidth={2.6} fill="currentColor" aria-hidden="true" />
              <span className="nse-play__label">
                Pause <span className="sr-only">the Smart Report showreel</span>
              </span>
            </>
          ) : (
            <>
              <span className="nse-play__disc" aria-hidden="true">
                <Play size={26} strokeWidth={2.4} fill="currentColor" />
              </span>
              <span className="nse-play__label">
                Play the Smart Report showreel <span className="nse-play__time">(0:30)</span>
              </span>
            </>
          )}
        </button>
      </div>
      <p className="nse-next__note">Smart Reports in 30 seconds · muted</p>
    </figure>
  );
}

export default function EndScreen({ status, active, panelRef, onBook }) {
  const navigate = useNavigate();
  const { visited } = useVisited();
  const score = exploreScore(visited);
  const booked = status === 'submitted';

  // R replays from the start, unless typing, in a media control or with a dialog open.
  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'r' && e.key !== 'R') return;
      if (e.defaultPrevented || e.repeat || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.target instanceof Element && e.target.closest(OWNS_KEYS)) return;
      if (document.querySelector(OPEN_DIALOG)) return;
      e.preventDefault();
      navigate('/');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, navigate]);

  return (
    <section
      ref={panelRef}
      className="nse nsd-end"
      tabIndex={-1}
      hidden={!active}
      aria-labelledby={active ? 'nsd-end-title' : undefined}
    >
      <div className="nse-bg" aria-hidden="true">
        <span className="nse-rays" />
        <span className="nse-floor" />
        {STREAKS.map(([angle, delay]) => (
          <span key={angle} className="nse-streak" style={{ '--a': `${angle}deg`, '--d': `${delay}s` }} />
        ))}
        <Circuits side="left" />
        <Circuits side="right" />
        <span className="nse-burst">
          {BURST.map(([angle, dist, size, hue]) => (
            <span
              key={angle}
              className="nse-burst__bit"
              style={{ '--a': `${angle}deg`, '--r': `${dist}px`, '--s': `${size}px`, '--c': hue }}
            />
          ))}
          <span className="nse-burst__ring" />
        </span>
      </div>

      <div className="nse-inner">
        <header className="nse-head">
          <p className="nse-badge">
            <Trophy size={15} strokeWidth={2.2} aria-hidden="true" />
            Demo complete
          </p>
          <Stars count={score.stars} />
          <div role="status" className="nse-status" data-trapezium-state={active ? status : undefined}>
            {active ? (
              <>
                <h2 id="nsd-end-title" className="nse-title">
                  See you <em>next time!</em>
                </h2>
                <p className="nse-line">{LINES[status]}</p>
              </>
            ) : null}
          </div>
        </header>

        <div className="nse-main">
          <div className="nse-you">
            <span className="nse-orb" aria-hidden="true">
              <span className="nse-orb__ring" />
              <span className="nse-orb__disc">
                <span className="nse-orb__mark">
                  <img src={LOGO_SRC} alt="" width="160" height="27" />
                </span>
              </span>
            </span>
            <p className="nse-you__name">NirogGyan</p>
            {booked ? (
              <p className="nse-sent">
                <Check size={18} strokeWidth={3} aria-hidden="true" />
                Demo request sent
              </p>
            ) : (
              <button
                type="button"
                className="nse-cta"
                onClick={onBook}
                data-trapezium-id="next-step.book-demo.end-book"
              >
                Book a live demo
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            )}
            <p className="nse-you__hint">
              {booked ? 'A 30-minute call on your own reports.' : '30 minutes, on your own reports.'}
            </p>
          </div>

          <div className="nse-run">
            <h3 className="nse-tag">Your run</h3>
            <ul className="nse-stats">
              <Stat name="pages" label="Pages explored" value={score.pages} total={score.totalPages} index={0} />
              <Stat
                name="products"
                label="Products explored"
                value={score.products}
                total={score.totalProducts}
                index={1}
              />
              <Stat
                name="sections"
                label="Sections covered"
                value={score.sections}
                total={score.totalSections}
                index={2}
              />
            </ul>
          </div>

          <WatchNext active={active} />
        </div>

        <footer className="nse-foot">
          <Link to="/" className="nse-replay" aria-keyshortcuts="R" data-trapezium-id="next-step.book-demo.start-again">
            <RotateCcw size={18} strokeWidth={2.4} aria-hidden="true" />
            Start again
            <kbd className="nse-key" aria-hidden="true">
              R
            </kbd>
          </Link>
          <a
            href={SITE}
            className="nse-site"
            target="_blank"
            rel="noopener noreferrer"
            data-trapezium-id="next-step.book-demo.site-link"
          >
            <Globe size={16} aria-hidden="true" />
            niroggyan.com
          </a>
        </footer>
      </div>
    </section>
  );
}
